import { Avatar, Box, Icon, Tag, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { DuplicateExpenseCardFragment } from 'generated/resources/graphql'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getStatusLabel } from 'modules/expenses/lib/expensables/statusLabel'
import tagColor from 'modules/expenses/lib/tagColors'
import { amountWithCurrency } from 'modules/finance/lib/budgets'

import './index.styles.css'

interface Props {
  expensable: DuplicateExpenseCardFragment
  onCompare: () => void
  onRemove: () => void
  isDiscarding: boolean
  locale: string
  type: 'duplicate_alerts' | 'per_diem_conflict'
}

const DuplicateExpensesCardContent = ({
  expensable,
  onCompare,
  onRemove,
  isDiscarding,
  locale,
  type,
}: Props) => {
  return (
    <Box
      className="_Content_CardContent"
      border={{
        all: {
          color: 'grey300',
          width: 's1',
          style: 'solid',
        },
      }}
      borderRadius={{ all: 'abs016' }}
    >
      <Box flexDirection="row" width="full">
        {expensable?.preview?.url && (
          <Box
            className="_Content_ImageContainer"
            textAlign="center"
            justifyContent="center"
            alignItems="center"
            border={{
              right: {
                color: 'grey300',
                width: 's1',
                style: 'solid',
              },
            }}
            borderRadius={{ topLeft: 'abs016', bottomLeft: 'abs016' }}
          >
            {/* eslint-disable-next-line factorial/no-raw-html */}
            <img
              src={buildUrl(expensable?.preview?.url)}
              alt={i18n.t('expenses_v2.expense.duplicate_alerts.thumbnail')}
              className="_Content_Image"
            />
          </Box>
        )}

        <Box gap="s12" paddingX="s16" paddingY="s12">
          <Box flexDirection="row" gap="s8" alignItems="center">
            {expensable.owner?.access && (
              <Avatar
                size="s24"
                url={buildUrl(expensable.owner.access.avatar)}
                label={expensable.owner.access.fullName}
              />
            )}
            <Text size="200" weight="medium" color="grey1000">
              {expensable.owner?.fullName}
            </Text>
            <Tag showCircle type={tagColor(expensable.status)} leftItem="circle" inline>
              {getStatusLabel(expensable.status)}
            </Tag>
          </Box>

          <Box flexDirection="row" gap="s16" width="full" alignItems="center">
            <Box flexDirection="row" gap="s6" alignItems="center">
              <Icon.Small icon="calendar" color="grey800" />
              <Text size="200">{dateBackendDateTimeToUI(expensable.effectiveOn)}</Text>
            </Box>
            <Box flexDirection="row" gap="s6" alignItems="center" maxWidth="s240">
              <Icon.Small icon="contract" color="grey800" />
              <Text size="200">
                {amountWithCurrency(Number(expensable.amount), expensable.currency, locale)}
              </Text>
              <Text size="200" ellipsis>
                {i18n.t('expenses_v2.expense.duplicate_alerts.reimbursable_amount', {
                  amount: amountWithCurrency(
                    Number(expensable.reimbursableAmount),
                    expensable.reimbursableCurrency ?? expensable.currency,
                    locale
                  ),
                })}
              </Text>
            </Box>
          </Box>

          <Box flexDirection="row" gap="s12" width="full" alignItems="center">
            <F0Button
              size="md"
              variant="outline"
              label={i18n.t('expenses_v2.expense.duplicate_alerts.compare')}
              onClick={() => {
                track(trackingEvents.expenses.compareExpenses, {
                  duplicate_expense_id: expensable.id,
                  duplicate_expense_status: expensable.status,
                })
                onCompare()
              }}
            />
            <F0Button
              size="md"
              variant="outline"
              label={
                type === 'duplicate_alerts'
                  ? i18n.t('expenses_v2.expense.duplicate_alerts.remove')
                  : i18n.t('expenses_v2.expense.per_diem_conflict_alerts.remove')
              }
              loading={isDiscarding}
              onClick={() => {
                onRemove()
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export { DuplicateExpensesCardContent }
